import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BarChart3,
  Download,
  FileText,
  TrendingUp,
  TrendingDown,
  Calendar,
  Users,
  AlertCircle,
  CheckCircle2,
  Clock
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [weeklyShifts, setWeeklyShifts] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [kpis, setKpis] = useState({
    weeklyFillRate: 0,
    shiftsUncovered: 0,
    staffUtilization: 0,
    openGaps: 0
  });

  useEffect(() => {
    fetchReportsData();
  }, []);

  const fetchReportsData = async () => {
    setLoading(true);
    try {
      // Fetch current week shifts (week starts Sunday — matches Overview & Schedule)
      const today = new Date();
      const sunday = getSunday(today);
      const weekStart = formatDate(sunday);
      
      const [shiftsResponse, teamResponse] = await Promise.all([
        axios.get(`${API_URL}/api/shifts/week/${weekStart}`),
        axios.get(`${API_URL}/api/team?active_only=true`)
      ]);

      setWeeklyShifts(shiftsResponse.data);
      setTeamMembers(teamResponse.data);

      // Calculate KPIs
      calculateKPIs(shiftsResponse.data, teamResponse.data);
    } catch (error) {
      console.error('Error fetching reports data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSunday = (date) => {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay()); // 0 = Sunday → most recent Sunday
    return d;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const calculateKPIs = (shiftsData, teamData) => {
    // Weekly Fill Rate
    const weeklyFillRate = shiftsData.average_fill_rate;

    // Shifts Uncovered This Week (required shifts only)
    const shiftsUncovered = shiftsData.daily_coverage.reduce((sum, day) => 
      sum + day.uncovered_required, 0
    );

    // Open Coverage Gaps (same as uncovered for now)
    const openGaps = shiftsUncovered;

    // Real Staff Utilization: total hours scheduled ÷ total max capacity.
    // Each covered shift counts as 8 hours (doubles count twice).
    // This uses the same formula as the Overview page so both screens stay in sync.
    const SHIFT_HOURS = 8;
    let totalScheduledHours = 0;
    shiftsData.daily_coverage.forEach((day) => {
      day.shifts.forEach((s) => {
        if (s.is_covered) totalScheduledHours += SHIFT_HOURS;
      });
    });
    const totalMaxHours = teamData.reduce(
      (sum, m) => sum + (m.max_hours_per_week || 0),
      0
    );
    const staffUtilization = totalMaxHours > 0
      ? Math.round((totalScheduledHours / totalMaxHours) * 100)
      : 0;

    setKpis({
      weeklyFillRate,
      shiftsUncovered,
      staffUtilization,
      openGaps
    });
  };

  const getCurrentWeekRange = () => {
    if (!weeklyShifts) return '';
    // Parse as local time (not UTC) to avoid US-timezone day shift
    const start = new Date(weeklyShifts.week_start + 'T00:00:00');
    const end = new Date(weeklyShifts.week_end + 'T00:00:00');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[start.getMonth()]} ${start.getDate()}-${end.getDate()}, ${end.getFullYear()}`;
  };

  // ==================== CSV EXPORT HELPERS ====================
  const SHIFT_META = {
    day:       { label: 'Day',       time: '7:00 AM – 3:00 PM' },
    swing:     { label: 'Swing',     time: '11:00 AM – 7:00 PM' },
    evening:   { label: 'Evening',   time: '3:00 PM – 11:00 PM' },
    overnight: { label: 'Overnight', time: '11:00 PM – 7:00 AM' },
  };
  const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Escape a value for CSV: wrap in quotes if it contains comma, quote, or newline; double internal quotes
  const csvEscape = (val) => {
    if (val === null || val === undefined) return '';
    const s = String(val);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const rowsToCSV = (rows) =>
    rows.map((row) => row.map(csvEscape).join(',')).join('\r\n');

  const triggerDownload = (csvText, filename) => {
    // Prepend BOM so Excel detects UTF-8 correctly
    const blob = new Blob(['\uFEFF' + csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const buildWeeklyCoverageRows = () => {
    if (!weeklyShifts) return [];
    const rows = [['Date', 'Day', 'Shift', 'Shift Time', 'Assigned RSS', 'Status', 'Daily Fill Rate', 'Double Shift']];
    weeklyShifts.daily_coverage.forEach((day, idx) => {
      // Pre-compute who is on 2+ shifts that day (= a double)
      const shiftsByName = {};
      day.shifts.forEach((s) => {
        if (s.assigned_rss_name) {
          shiftsByName[s.assigned_rss_name] = (shiftsByName[s.assigned_rss_name] || 0) + 1;
        }
      });
      day.shifts.forEach((shift) => {
        const meta = SHIFT_META[shift.shift_type] || { label: shift.shift_type, time: '' };
        const isDouble = shift.assigned_rss_name && shiftsByName[shift.assigned_rss_name] >= 2;
        rows.push([
          day.date,
          DAY_NAMES[idx] || '',
          meta.label,
          meta.time,
          shift.assigned_rss_name || 'Uncovered',
          shift.is_covered ? 'Covered' : 'Uncovered',
          `${day.fill_rate}%`,
          isDouble ? 'YES' : '',
        ]);
      });
    });
    return rows;
  };

  const buildStaffAvailabilityRows = () => {
    const rows = [['Name', 'Employment Type', 'Preferred Shifts', 'Max Hours/Week', 'Availability Notes']];
    teamMembers.forEach((m) => {
      const shifts = (m.preferred_shifts || []).map((s) => SHIFT_META[s]?.label || s).join('; ');
      rows.push([
        m.name,
        (m.staff_type || '').toUpperCase(),
        shifts || 'No preference',
        m.max_hours_per_week,
        m.availability_notes || '',
      ]);
    });
    return rows;
  };

  const buildUncoveredShiftRows = () => {
    if (!weeklyShifts) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const rows = [['Date', 'Day', 'Shift', 'Shift Time', 'Status', 'Days Uncovered']];
    weeklyShifts.daily_coverage.forEach((day, idx) => {
      day.shifts.forEach((shift) => {
        if (shift.is_covered) return;
        // Only flag REQUIRED shifts as gaps (Day, Evening, Overnight); skip optional Swing
        if (shift.shift_type === 'swing') return;
        const meta = SHIFT_META[shift.shift_type] || { label: shift.shift_type, time: '' };
        const shiftDate = new Date(day.date);
        const diffDays = Math.floor((today - shiftDate) / (1000 * 60 * 60 * 24));
        rows.push([
          day.date,
          DAY_NAMES[idx] || '',
          meta.label,
          meta.time,
          'Uncovered',
          diffDays > 0 ? diffDays : 0,
        ]);
      });
    });
    return rows;
  };

  const handleExportWeeklyCoverage = () => {
    const csv = rowsToCSV(buildWeeklyCoverageRows());
    triggerDownload(csv, `weekly-coverage-${weeklyShifts?.week_start || 'report'}.csv`);
  };

  const handleExportStaffAvailability = () => {
    const csv = rowsToCSV(buildStaffAvailabilityRows());
    triggerDownload(csv, `staff-availability-${formatDate(new Date())}.csv`);
  };

  const handleExportUncoveredShifts = () => {
    const csv = rowsToCSV(buildUncoveredShiftRows());
    triggerDownload(csv, `uncovered-shifts-${weeklyShifts?.week_start || 'report'}.csv`);
  };

  const handleExportAll = () => {
    const sections = [
      ['ALIGN 3.1 Ops Manager — Combined Reports'],
      [`Generated: ${new Date().toISOString()}`],
      [`Week: ${getCurrentWeekRange()}`],
      [''],
      ['=== WEEKLY COVERAGE REPORT ==='],
      ...buildWeeklyCoverageRows(),
      [''],
      ['=== STAFF AVAILABILITY SUMMARY ==='],
      ...buildStaffAvailabilityRows(),
      [''],
      ['=== UNCOVERED SHIFT LOG ==='],
      ...buildUncoveredShiftRows(),
    ];
    const csv = rowsToCSV(sections);
    triggerDownload(csv, `align-3.1-all-reports-${weeklyShifts?.week_start || formatDate(new Date())}.csv`);
  };

  const reports = [
    {
      id: 1,
      title: 'Weekly Coverage Report',
      description: 'Comprehensive shift coverage analysis for current week',
      date: getCurrentWeekRange(),
      type: 'Coverage',
      status: 'Ready',
      icon: Calendar,
      onExport: handleExportWeeklyCoverage,
      testId: 'export-weekly-coverage',
    },
    {
      id: 2,
      title: 'Staff Availability Summary',
      description: 'RSS staff availability and shift preferences overview',
      date: getCurrentWeekRange(),
      type: 'Staff',
      status: 'Ready',
      icon: Users,
      onExport: handleExportStaffAvailability,
      testId: 'export-staff-availability',
    },
    {
      id: 3,
      title: 'Uncovered Shift Log',
      description: 'List of all uncovered shifts requiring immediate attention',
      date: 'May 2026',
      type: 'Coverage',
      status: 'Ready',
      icon: AlertCircle,
      onExport: handleExportUncoveredShifts,
      testId: 'export-uncovered-shifts',
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            3.1 Program Coverage Analytics & Staff Reports
          </p>
        </div>
        <Button onClick={handleExportAll} data-testid="export-all-button">
          <Download className="h-4 w-4 mr-2" />
          Export All
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Weekly Fill Rate */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Weekly Fill Rate</h3>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-3xl font-bold">{kpis.weeklyFillRate}%</div>
            <div className="flex items-center gap-1 text-xs">
              {kpis.weeklyFillRate >= 75 ? (
                <>
                  <TrendingUp className="h-3 w-3 text-green-600" />
                  <span className="text-green-600">Good coverage</span>
                </>
              ) : (
                <>
                  <TrendingDown className="h-3 w-3 text-yellow-600" />
                  <span className="text-yellow-600">Needs improvement</span>
                </>
              )}
            </div>
          </div>
        </Card>

        {/* Shifts Uncovered */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Uncovered This Week</h3>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-3xl font-bold">{kpis.shiftsUncovered}</div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>Required shifts</span>
            </div>
          </div>
        </Card>

        {/* Staff Utilization */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Staff Utilization</h3>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-3xl font-bold">{kpis.staffUtilization}%</div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>Hours worked vs. capacity</span>
            </div>
          </div>
        </Card>

        {/* Open Coverage Gaps */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                <Clock className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Open Coverage Gaps</h3>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-3xl font-bold">{kpis.openGaps}</div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>Needs assignment</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Reports */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Available Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reports.map((report, index) => (
            <motion.div
              key={report.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="p-5 hover:shadow-lg transition-shadow cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <report.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-base">{report.title}</h3>
                      <Badge variant="secondary" className="ml-2">
                        {report.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {report.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <FileText className="h-3 w-3" />
                        <span>{report.type}</span>
                        <span>•</span>
                        <span>{report.date}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={report.onExport}
                        data-testid={report.testId}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Export
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Weekly Breakdown */}
      {weeklyShifts && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Weekly Shift Breakdown</h2>
            <Badge variant="secondary">{getCurrentWeekRange()}</Badge>
          </div>
          <div className="space-y-3">
            {weeklyShifts.daily_coverage.map((day, index) => {
              const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
              return (
                <div key={day.date} className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div className="font-medium">{dayNames[index]}</div>
                    <Badge variant={day.fill_rate >= 100 ? 'default' : 'secondary'}>
                      {day.fill_rate}% covered
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {day.covered_required}/{day.required_shifts} required shifts
                    {day.uncovered_required > 0 && (
                      <span className="text-red-600 ml-2">
                        ({day.uncovered_required} uncovered)
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Staff Summary */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">RSS Staff Summary</h2>
          <Badge variant="secondary">{teamMembers.length} active</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 rounded-lg bg-muted">
            <div className="text-sm text-muted-foreground mb-1">Full-Time</div>
            <div className="text-2xl font-bold">
              {teamMembers.filter(m => m.staff_type === 'ft').length}
            </div>
          </div>
          <div className="p-4 rounded-lg bg-muted">
            <div className="text-sm text-muted-foreground mb-1">PRN</div>
            <div className="text-2xl font-bold">
              {teamMembers.filter(m => m.staff_type === 'prn').length}
            </div>
          </div>
          <div className="p-4 rounded-lg bg-muted">
            <div className="text-sm text-muted-foreground mb-1">Total Max Hours</div>
            <div className="text-2xl font-bold">
              {teamMembers.reduce((sum, m) => sum + m.max_hours_per_week, 0)}h
            </div>
          </div>
          <div className="p-4 rounded-lg bg-muted">
            <div className="text-sm text-muted-foreground mb-1">Avg Hours/Staff</div>
            <div className="text-2xl font-bold">
              {teamMembers.length > 0 ? Math.round(teamMembers.reduce((sum, m) => sum + m.max_hours_per_week, 0) / teamMembers.length) : 0}h
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Reports;
